/**
 * Structural parser for the constrained math grammar (CLAUDE.md 21.2, ADR-0007).
 *
 * This does NOT evaluate. It answers two questions the dependency graph needs before
 * anything runs: what does this cell define, and what does it read? Evaluation is
 * SymPy's job, in the worker.
 *
 * Keeping the two apart is what makes the graph synchronously testable without a
 * 10 MB WASM runtime in the test process -- and it means a malformed cell is
 * diagnosed as a syntax error in microseconds rather than after a worker round trip.
 *
 * Grammar (deliberately small; 21.2's "constrained expression grammar"):
 *
 *   x := expr        assignment
 *   x = expr         assignment, when the left side is a single symbol
 *   expr =           evaluate and display
 *   expr             evaluate and display
 *
 * A trailing `=` anywhere means "show the result", which is how Mathcad users write.
 * Anything else with `=` on both sides is an equation, which v1 does not solve, and
 * says so rather than guessing.
 */
import type { MathSymbol } from './types'
import { symbolKey } from './types'

/** Greek command names that denote an identifier rather than an operator. */
const GREEK = new Set([
  'alpha',
  'beta',
  'gamma',
  'delta',
  'epsilon',
  'varepsilon',
  'zeta',
  'eta',
  'theta',
  'vartheta',
  'iota',
  'kappa',
  'lambda',
  'mu',
  'nu',
  'xi',
  'omicron',
  'pi',
  'varpi',
  'rho',
  'varrho',
  'sigma',
  'varsigma',
  'tau',
  'upsilon',
  'phi',
  'varphi',
  'chi',
  'psi',
  'omega',
  'Gamma',
  'Delta',
  'Theta',
  'Lambda',
  'Xi',
  'Pi',
  'Sigma',
  'Upsilon',
  'Phi',
  'Psi',
  'Omega',
])

/**
 * Commands whose braced argument is not mathematics: unit symbols and prose. The
 * argument is consumed whole and never scanned for identifiers, so `\mathrm{kN}`
 * is a unit and not the product of k and N. This one rule is why a units-aware
 * sheet can tell a variable from a kilonewton at all.
 */
const OPAQUE = new Set(['mathrm', 'text', 'textrm', 'textit', 'operatorname', 'unit', 'mathsf'])

/** Commands that carry structure but reference nothing. */
const STRUCTURAL = new Set([
  'frac',
  'dfrac',
  'tfrac',
  'sqrt',
  'cdot',
  'times',
  'div',
  'left',
  'right',
  'quad',
  'qquad',
  'displaystyle',
  'pm',
  'mp',
  'ldots',
  'dots',
  'cdots',
  'big',
  'Big',
  'bigl',
  'bigr',
  'exp',
  'ln',
  'log',
  'sin',
  'cos',
  'tan',
  'arcsin',
  'arccos',
  'arctan',
  'sinh',
  'cosh',
  'tanh',
  'abs',
  'min',
  'max',
  'lvert',
  'rvert',
  'langle',
  'rangle',
])

export type Token =
  | { readonly t: 'sym'; readonly sym: MathSymbol; readonly key: string }
  | { readonly t: 'env'; readonly begin: boolean; readonly name: string }
  | { readonly t: 'num'; readonly text: string }
  | { readonly t: 'unit'; readonly text: string }
  | { readonly t: 'op'; readonly text: string }
  | { readonly t: 'assign' }
  | { readonly t: 'eq' }

export type ParsedMath =
  | { readonly kind: 'empty' }
  | { readonly kind: 'assign'; readonly target: string; readonly uses: readonly string[] }
  | { readonly kind: 'evaluate'; readonly uses: readonly string[] }
  | {
      readonly kind: 'error'
      readonly messageKey: string
      readonly params?: Readonly<Record<string, string | number>>
    }

/** Reads a `{...}` group starting at `i` (which must index the `{`). */
function readGroup(src: string, i: number): { text: string; next: number } {
  let depth = 0
  let out = ''
  let k = i
  for (; k < src.length; k += 1) {
    const ch = src[k]
    if (ch === '{') {
      depth += 1
      if (depth === 1) continue
    } else if (ch === '}') {
      depth -= 1
      if (depth === 0) return { text: out, next: k + 1 }
    }
    if (depth >= 1) out += ch
  }
  // Unbalanced: consume the rest so the tokenizer terminates.
  return { text: out, next: src.length }
}

/** Strips LaTeX decoration from subscript text: `\mathrm{Ed}` and `{Ed}` both give `Ed`. */
function cleanSub(raw: string): string {
  return raw
    .replace(/\\[a-zA-Z]+/g, '')
    .replace(/[{}\s]/g, '')
    .replace(/,+/g, ',')
    .replace(/^,|,$/g, '')
}

export function tokenize(latex: string): Token[] {
  const out: Token[] = []
  let i = 0

  /** Attaches a subscript to the symbol just pushed, if one follows. */
  const attachSub = (base: string): void => {
    let sub = ''
    if (latex[i] === '_') {
      i += 1
      if (latex[i] === '{') {
        const g = readGroup(latex, i)
        sub = cleanSub(g.text)
        i = g.next
      } else {
        const ch = latex[i]
        if (ch !== undefined) {
          sub = ch
          i += 1
        }
      }
    }
    const sym: MathSymbol = { base, sub }
    out.push({ t: 'sym', sym, key: symbolKey(sym) })
  }

  while (i < latex.length) {
    const ch = latex[i]
    if (ch === undefined) break

    if (ch === ' ' || ch === '\n' || ch === '\t' || ch === '~') {
      i += 1
      continue
    }

    if (ch === '\\') {
      // A row separator inside a matrix environment. Checked before the command
      // pattern, which cannot match a second backslash.
      if (latex[i + 1] === '\\') {
        out.push({ t: 'op', text: 'row' })
        i += 2
        continue
      }
      const m = /^\\([a-zA-Z]+)/.exec(latex.slice(i))
      if (!m || m[1] === undefined) {
        // An escaped punctuation such as `\,` or `\;`: spacing, no meaning here.
        i += 2
        continue
      }
      const name = m[1]
      i += m[0].length

      if (name === 'begin' || name === 'end') {
        while (latex[i] === ' ') i += 1
        if (latex[i] === '{') {
          const g = readGroup(latex, i)
          i = g.next
          out.push({ t: 'env', begin: name === 'begin', name: g.text.trim() })
        }
        continue
      }
      if (OPAQUE.has(name)) {
        // Skip a following optional group entirely -- it is a unit or prose.
        while (latex[i] === ' ') i += 1
        if (latex[i] === '{') {
          const g = readGroup(latex, i)
          i = g.next
          out.push({ t: 'unit', text: g.text.trim() })
        }
        continue
      }
      if (GREEK.has(name)) {
        attachSub(name)
        continue
      }
      if (STRUCTURAL.has(name)) {
        out.push({ t: 'op', text: name })
        continue
      }
      // Unknown command: treat as structure rather than inventing a variable.
      out.push({ t: 'op', text: name })
      continue
    }

    if (ch >= '0' && ch <= '9') {
      const m = /^\d+(?:\.\d+)?/.exec(latex.slice(i))
      const text = m?.[0] ?? ch
      out.push({ t: 'num', text })
      i += text.length
      continue
    }

    if (/[a-zA-Z]/.test(ch)) {
      i += 1
      attachSub(ch)
      continue
    }

    if (ch === ':' && latex[i + 1] === '=') {
      out.push({ t: 'assign' })
      i += 2
      continue
    }

    if (ch === '=') {
      out.push({ t: 'eq' })
      i += 1
      continue
    }

    out.push({ t: 'op', text: ch })
    i += 1
  }

  return out
}

/** Index of the first `assign`/`eq` token outside any brace group. */
function topLevelSplit(tokens: readonly Token[]): number {
  let depth = 0
  for (let k = 0; k < tokens.length; k += 1) {
    const tok = tokens[k]
    if (tok === undefined) continue
    if (tok.t === 'env') {
      depth += tok.begin ? 1 : -1
      continue
    }
    if (tok.t === 'op') {
      if (tok.text === '{' || tok.text === '(' || tok.text === '[') depth += 1
      else if (tok.text === '}' || tok.text === ')' || tok.text === ']') depth -= 1
      continue
    }
    if (depth === 0 && (tok.t === 'assign' || tok.t === 'eq')) return k
  }
  return -1
}

function symbolsIn(tokens: readonly Token[]): string[] {
  const seen: string[] = []
  for (const tok of tokens) {
    if (tok.t === 'sym' && !seen.includes(tok.key)) seen.push(tok.key)
  }
  return seen
}

/** True when the tokens are exactly one symbol -- a valid assignment target. */
function loneSymbol(tokens: readonly Token[]): string | null {
  const meaningful = tokens.filter((tok) => !(tok.t === 'op' && /^[{}\s]$/.test(tok.text)))
  const first = meaningful[0]
  if (meaningful.length === 1 && first !== undefined && first.t === 'sym') return first.key
  return null
}

export function parseMath(latex: string): ParsedMath {
  let tokens = tokenize(latex)
  if (tokens.length === 0) return { kind: 'empty' }

  // A trailing `=` asks for the result to be shown. Every cell shows its result
  // anyway, so it carries no meaning beyond being the notation Mathcad users type --
  // strip it rather than let it look like an empty right-hand side.
  const last = tokens[tokens.length - 1]
  if (last !== undefined && last.t === 'eq') {
    tokens = tokens.slice(0, -1)
    if (tokens.length === 0) return { kind: 'empty' }
  }

  const cut = topLevelSplit(tokens)
  if (cut < 0) {
    return { kind: 'evaluate', uses: symbolsIn(tokens) }
  }

  const opTok = tokens[cut]
  const lhs = tokens.slice(0, cut)
  const rhs = tokens.slice(cut + 1)

  if (rhs.length === 0) {
    // `x =` -- display the value of x.
    return { kind: 'evaluate', uses: symbolsIn(lhs) }
  }

  const target = loneSymbol(lhs)

  if (opTok !== undefined && opTok.t === 'assign') {
    if (target === null) {
      return { kind: 'error', messageKey: 'sheets.error.assign_target' }
    }
    return { kind: 'assign', target, uses: symbolsIn(rhs) }
  }

  // A bare `=` with content on both sides. One symbol on the left is the ergonomic
  // assignment engineers actually write; anything else is an equation, and v1 does
  // not solve equations -- so say that rather than guess which side is the unknown.
  if (target === null) {
    return { kind: 'error', messageKey: 'sheets.error.equation_unsupported' }
  }
  // An assignment written with `=` always shows its result, with or without the
  // trailing `=` that Mathcad requires.
  return { kind: 'assign', target, uses: symbolsIn(rhs) }
}

/**
 * The tokens a cell's *value* comes from -- what the compiler should be handed.
 *
 * A cell carries its assignment operator and target, and those are structure rather
 * than arithmetic: giving `M_Ed := w L^2 / 8` to the compiler whole fails on the
 * `:=`. This shares `tokenize` and `topLevelSplit` with `parseMath`, so the two can
 * never disagree about where a cell divides.
 */
export function expressionTokens(latex: string): Token[] {
  let tokens = tokenize(latex)
  if (tokens.length === 0) return []

  const last = tokens[tokens.length - 1]
  if (last !== undefined && last.t === 'eq') tokens = tokens.slice(0, -1)
  if (tokens.length === 0) return []

  const cut = topLevelSplit(tokens)
  if (cut < 0) return tokens

  const rhs = tokens.slice(cut + 1)
  // `x =` asks for the value of x, so there the expression is the left side.
  return rhs.length === 0 ? tokens.slice(0, cut) : rhs
}
